import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useForm } from 'react-hook-form';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

// components

import { Modal } from 'app-components';
import GIButton from '@common-components/button/GIButton';

// props

interface ResetPasswordModalProps {
    hhId: string;
    onClose: () => void;
    onClickHandler: (hhid: any, data: FormValues) => void;
}

// types

type FormValues = {
    email: string;
    alternateEmail: string;
    reason: string;
};

function ResetPasswordModal({ hhId, onClose, onClickHandler }: ResetPasswordModalProps) {
    const {
        register,
        handleSubmit,
        watch,
        setValue,
        clearErrors,
        formState: { errors },
    } = useForm<FormValues>({ defaultValues: {}, mode: 'onChange' });

    const intl = useIntl();

    const onClickSendHandler = async (data: FormValues) => {
        onClickHandler(hhId, data);
    };

    const btnDisabled = watch('email', '');
    const onCurrentEmailSelection = e => {
        setValue('email', e.target.value);
        clearErrors();
    };

    return (
        <form onSubmit={handleSubmit(onClickSendHandler)}>
            <Modal id="account_activation_email_modal">
                <Modal.Header
                    id="account_activation_email_modal_title"
                    titleId="reset_password.modal.header"
                    onHide={onClose}
                />
                <Modal.Body>
                    <fieldset className="usa-fieldset gi-radio_buttons">
                        <legend className="usa-legend control-label required-true">
                            <FormattedMessage id="reset_password.modal.description" />
                        </legend>
                        <div className="usa-radio">
                            <input
                                {...register('email')}
                                type="radio"
                                value={'hixEmail'}
                                onChange={onCurrentEmailSelection}
                                id="field-currentEmail"
                                className="usa-radio__input gi-radio_buttons__input"
                            />
                            <label className="usa-radio__label gi-radio_buttons__label" htmlFor="field-currentEmail">
                                <FormattedMessage id="reset_password.modal.radio.currentEmail" />
                            </label>
                        </div>
                        <div className="usa-radio">
                            <input
                                {...register('email')}
                                type="radio"
                                value={'alternateEmail'}
                                id="field-alternateEmail"
                                className="usa-radio__input gi-radio_buttons__input"
                            />
                            <label className="usa-radio__label gi-radio_buttons__label" htmlFor="field-alternateEmail">
                                <FormattedMessage id="reset_password.modal.radio.alternateEmail" />
                            </label>
                        </div>
                    </fieldset>
                    {btnDisabled === 'alternateEmail' && (
                        <input
                            type="text"
                            className="ml-5"
                            {...register('alternateEmail', {
                                required: {
                                    value: true,
                                    message: intl.formatMessage({
                                        id: 'reset_password.modal.alternate.email.required',
                                    }),
                                },
                                pattern: {
                                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                                    message: intl.formatMessage({
                                        id: 'reset_password.modal.alternate.email.required',
                                    }),
                                },
                            })}
                        />
                    )}

                    {btnDisabled === 'alternateEmail' && (
                        <div className="ml-5">
                            {errors?.alternateEmail && (
                                <p className="shown error--msg">
                                    <em>
                                        <FontAwesomeIcon icon="exclamation" />
                                    </em>
                                    {errors?.alternateEmail?.message}
                                </p>
                            )}
                        </div>
                    )}
                    {btnDisabled === 'alternateEmail' && (
                        <div className="mt-5">
                            <div className="control-label required-true">
                                <FormattedMessage id="reset_password.modal.alternate.email.reason" />
                            </div>
                            <input
                                type="text"
                                className="mt-4 control-label container"
                                {...register('reason', {
                                    required: {
                                        value: true,
                                        message: intl.formatMessage({
                                            id: 'reset_password.modal.alternate.email.reason.required',
                                        }),
                                    },
                                })}
                            />
                            {errors?.reason && (
                                <div className="shown error--msg">
                                    <em>
                                        <FontAwesomeIcon icon="exclamation" />
                                    </em>
                                    {errors?.reason?.message}{' '}
                                </div>
                            )}
                        </div>
                    )}

                    <div className="usa-alert gi-alert gi-alert__left-accent gi-alert__info">
                        <div className="gi-alert__body">
                            <p className="gi-alert__text">
                                <FormattedMessage id="reset_password.modal.alert.message" />
                            </p>
                        </div>
                    </div>
                </Modal.Body>
                <Modal.Controls>
                    <GIButton
                        id="cancel"
                        labelId="common.cancel"
                        variant="secondary"
                        onClickHandler={onClose}
                    ></GIButton>
                    <GIButton
                        type={'submit'}
                        id="member_view"
                        labelId="button.submit"
                        variant="primary"
                        isDisabled={!btnDisabled}
                    ></GIButton>
                </Modal.Controls>
            </Modal>
        </form>
    );
}

export default ResetPasswordModal;
